import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { type OrgContext } from "@/lib/context.ts"
import { resetErrorOnChange, submitGeneric } from "@/lib/formUtils.ts"
import { Model } from "@/lib/Model.ts"

import type { ChangeEvent } from "react"

export type TextFieldModelParams = {
  readonly context: OrgContext
  readonly value: string
  readonly taskId: string
  readonly editable: boolean
  readonly placeholder: string
  readonly field: string
}

export abstract class TextFieldModel extends Model {
  readonly originalValue: string
  readonly value = observable.box("")
  readonly hasChanges = computed(() => {
    return this.value.get().trim() !== this.originalValue
  })
  readonly isSaving = observable.box(false)
  readonly context: OrgContext
  readonly error = observable.box(null)
  readonly canSubmit = computed(() => {
    return this.value.get().length > 0 && this.error.get() === null && this.hasChanges.get()
  })
  readonly taskId: string
  readonly editable = observable.box(true)
  readonly placeholder: string
  readonly field: string

  protected constructor(params: TextFieldModelParams & { name: string }) {
    super(params.name)
    this.value.set(params.value)
    this.context = params.context
    this.originalValue = params.value
    this.taskId = params.taskId
    this.editable.set(params.editable)
    this.placeholder = params.placeholder
    this.field = params.field

    resetErrorOnChange(this.lifetime, this.error, this.value)
  }

  readonly handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      this.value.set(e.target.value)
    })
  }

  readonly abstract validate: () => Promise<string | null>
  readonly abstract submitImpl: () => Promise<void>

  save = async () => {
    await submitGeneric({
      submit: () => this.submitImpl(),
      context: this.context,
      isSubmitting: this.isSaving,
      error: this.error,
      validate: this.validate
    })
  }

  cancel = () => {
    runInAction(() => {
      this.value.set(this.originalValue)
    })
  }
}

export class RepositoryUrlMessageModel extends TextFieldModel {
  constructor(params: TextFieldModelParams) {
    super({
      ...params,
      name: "RepositoryUrlMessageModel"
    })
  }

  readonly validate = async () => {
    const validationResult = await this.context.queries.validateRemoteUrl(this.value.get())
    if (validationResult.valid) {
      return null
    } else {
      return validationResult.error ?? "Invalid URL"
    }
  }

  readonly submitImpl = async () => {
    await this.context.queries.apiClient.setRepositoryUrl(this.taskId, this.value.get().trim())
    toast.info(`Repository URL updated: ${this.value.get().trim()}`)
  }
}